// Pure row/meta strings for the Clients directory (mobile overhaul Phase C
// §7.1 A). Desktop and the ≤880px branch share them so both renders always
// say the same thing about a client — and so the strings can be tested
// without a DOM (the suite is Node-only).

import { PHASE_PILL_LABELS, type ClientStatus, type ProjectPhase } from '../../shared/types';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "Jun 26" — month + 2-digit year, the directory's last-activity column. */
export function monthYear(iso: string | null): string {
  if (!iso) return '—';
  return `${MONTHS[Number(iso.slice(5, 7)) - 1]} ${iso.slice(2, 4)}`;
}

export function projectsLabel(n: number): string {
  return `${n} project${n === 1 ? '' : 's'}`;
}

/** Active-now row meta: the live project and its phase pill label. */
export function clientActiveMeta(client: {
  active_project_name: string | null;
  active_project_phase: ProjectPhase | null;
}): { project: string; phase: string | null } {
  return {
    project: client.active_project_name || '—',
    phase: client.active_project_phase ? PHASE_PILL_LABELS[client.active_project_phase] : null,
  };
}

/** Directory row meta: "Karen Doyle · karen@2inspire.com.au", or "—". */
export function clientDirectoryMeta(client: {
  contact_name: string | null;
  contact_email: string | null;
}): string {
  return [client.contact_name, client.contact_email].filter(Boolean).join(' · ') || '—';
}

/** Right column: project count, over the last-project month on directory
    rows (an active-now row already says which project is live). */
export function clientRightMeta(
  client: { projects_total: number; last_project_date: string | null },
  activeNow: boolean,
): { projects: string; last: string | null } {
  return {
    projects: projectsLabel(client.projects_total),
    last: activeNow ? null : monthYear(client.last_project_date),
  };
}

/** The mono line under the chips — two spans so each keeps its own tone
    (left accent, right gold). */
export function clientVitals(activeNow: number, newFY: number, repeat: number): { left: string; right: string } {
  return { left: `${activeNow} ACTIVE NOW · ${newFY} NEW THIS FY`, right: `${repeat} REPEAT` };
}

export interface ClientListFilter {
  view: 'all' | 'active' | 'past';
  status: string | null;
  query: string;
}

/** The list's visible set. The FilterSheet's "Show N clients" footer counts
    a *draft* filter with this too, so the number always matches what Apply
    would produce. */
export function filterClients<T extends { name: string; status: ClientStatus; contact_name: string | null; contact_email: string | null }>(
  clients: T[],
  { view, status, query }: ClientListFilter,
): T[] {
  let list = clients;
  if (view === 'active') list = list.filter((c) => c.status === 'active');
  if (view === 'past') list = list.filter((c) => c.status === 'past' || c.status === 'archived');
  if (status) list = list.filter((c) => c.status === status);
  const q = query.trim().toLowerCase();
  if (q) {
    list = list.filter((c) =>
      [c.name, c.contact_name, c.contact_email].filter(Boolean).some((s) => (s as string).toLowerCase().includes(q)),
    );
  }
  return list;
}
